import Resolver from "@/api/resolver";
import { ZERO_TIME } from "@/components/shared/util";
import { OpUnitType } from "dayjs";

export function humanReadable(seconds: number, decimal = 1): string {
  let value = "";
  let unit = "";
  // Expands the range of the bucketing to include 50% more than the size
  // Up to 90 seconds is 90 seconds instead of 1 min
  const stepUpMultiplier = 1.5;

  const times: Record<string, number> = {
    year: 31557600,
    day: 86400,
    hour: 3600,
    min: 60
  };
  let found = false;
  Object.keys(times).forEach(key => {
    const compare = times[key];
    if (!found && seconds >= compare * stepUpMultiplier) {
      unit = key;
      value = (seconds / compare).toFixed(decimal);
      found = true;
    }
  });

  if (seconds <= 60 * stepUpMultiplier) {
    unit = "sec";
    value = seconds.toString();
  }

  const plural = Number.parseFloat(value) > 1 ? "s" : "";
  return `${value} ${unit}${plural}`;
}

export function age(dob: string, now: string) {
  if (!dob || dob === ZERO_TIME) {
    return "Unknown";
  }

  const ageYears = dateDiff(dob, now, "year", true);
  // DOB in future
  if (ageYears < 0) {
    return "Unknown";
  }

  if (ageYears >= 2) {
    const flooredAge = Math.floor(ageYears);
    return `${flooredAge} years`;
  }
  const ageMonths = dateDiff(dob, now, "month", true);
  if (ageMonths >= 1) {
    const flooredAge = Math.floor(ageMonths);
    const plural = flooredAge === 1 ? "" : "s";
    return `${flooredAge} month${plural}`;
  } else {
    const ageDays = dateDiff(dob, now, "day", true);

    const flooredAge = Math.floor(ageDays);
    const plural = flooredAge === 1 ? "" : "s";

    return `${flooredAge} day${plural} old`;
  }
}

export function dateDiff(
  from: string,
  to: string,
  measure: OpUnitType = "millisecond",
  fraction = false
): number {
  const fromDate = parseServerTime(from);
  const toDate = parseServerTime(to);
  return toDate.diff(fromDate, measure, fraction);
}

export function ageHumanReadable(from: string, to: string) {
  return age(from, to);
}

export function dateDiffHumanReadable(from: string, to: string, decimal = 1) {
  const fromDate = parseServerTime(from);
  const toDate = parseServerTime(to);
  const seconds = toDate.diff(fromDate, "second");
  return humanReadable(Math.abs(seconds), decimal);
}

export function timeAgo(from: string, to: string) {
  const result = dateDiff(from, to, "minute");
  return Math.floor(result / 60) + ":" + twoDigit(result % 60);
}

function twoDigit(s: number) {
  if (s < 10) {
    return "0" + s;
  }
  return s;
}

// FUTURE: Send date time offset from server
function convertServerTime(time: string) {
  return time + Resolver.dateTimeOffset;
}

// Used by charting to convert a set of points
export function convertServerDataPoints(data: any) {
  return data.map((d: any) => {
    return { x: parseServerTime(d.time), y: d.value };
  });
}

// Parse a time string from the server into dayjs object, will handle time offset
export function parseServerTime(time: string) {
  return Resolver.parseDate(convertServerTime(time));
}

// When the data was recorded, use this to compare to other server times
export function serverNow() {
  return Resolver.serverNow();
}

export function serverOffset() {
  return Resolver.offsetInMinutes;
}

// Used for timing events, only compare to other localNow times
export function localNow() {
  return Resolver.localNow();
}
